import React, { useEffect, useState } from 'react';
import {
	Box,
	Grid,
	Typography,
	Button,
	Avatar,
	Card,
	CardContent,
	Alert,
} from '@mui/material';
import { StyleSheet, css } from 'aphrodite';
import Sidebar from './dashboard_sections/sidebar.js';
import DashboardStats from './dashboard_sections/dashboard-stats.js';
import PatientRecords from './dashboard_sections/patientrecords';
import RecentActivity from './dashboard_sections/recent-activity.js';
import TreatmentRecords from './dashboard_sections/treatmentrecords.js';
import AppointmentsCalendar from './dashboard_sections/appointments-calender.js';
import { checkProfileCompletionStatus } from '../../../utils/config/helpers';
import { useInitializeAuthentication } from '../../../hooks/useInitializeAuthentication';
import { useInitializeCredentials } from '../../../hooks/useInitializeCredentials';
import { useHospitalAuth } from '../../../hooks/useHospitalAuth';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { APP_URLS } from '../../../../urls';

export default function DashboardHome() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
	const [isLoading, setIsLoading] = useState(false);
	const [isProfileComplete, setIsProfileComplete] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');

	const { isAuthenticated, credentials, hospitalData } = useHospitalAuth(
		true,
		true,
	);

	const mergedHospitalData = { ...hospitalAuthData, ...hospitalData };
	// console.log('mergedHospitalData: ', mergedHospitalData);

	const { patient: hospitalAuthData } = useSelector(
		(state) => state.hospitalAuth,
	);

	useEffect(() => {
		if (!isAuthenticated) {
			navigate(APP_URLS.hospitalLogin);
		}
	}, [isAuthenticated, navigate]);

	useEffect(() => {
		const checkProfileStatus = async () => {
			setIsLoading(true);
			try {
				const { isComplete, error } =
					await checkProfileCompletionStatus(credentials);
				if (error) {
					setErrorMessage('Profile is incomplete. Please complete it.');
					return;
				}
				setIsProfileComplete(isComplete);
			} catch (err) {
				console.error('Profile check error:', err);
				setErrorMessage('Failed to check profile status');
			} finally {
				setIsLoading(false);
			}
		};

		if (credentials) {
			checkProfileStatus();
		}
	}, [credentials]);

	const handleCompleteProfile = () => {
		navigate(APP_URLS.hospitalProfile);
	};

	return (
		<div className={css(styles.container)}>
			{/* Sidebar */}
			<Sidebar
				isSidebarExpanded={isSidebarExpanded}
				toggleSidebarExpansion={() => setIsSidebarExpanded(!isSidebarExpanded)}
			/>

			{/* Main Content */}
			<div
				className={css(
					styles.content,
					isSidebarExpanded && styles.contentShift,
				)}
			>
				<Typography variant="h4" className={css(styles.title)}>
					<h2>Welcome Back!</h2>
				</Typography>
				{/* Profile Completion Alert */}
				{!isProfileComplete && !isLoading && (
					<Alert
						severity="info"
						sx={{
							mb: 2,
							mx: { xs: 1, sm: 4 },
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
						action={
							<Button
								color="inherit"
								size="small"
								onClick={handleCompleteProfile}
								sx={{
									backgroundColor: '#00796b',
									color: 'white',
									'&:hover': { backgroundColor: '#005f56' },
								}}
							>
								Complete Now
							</Button>
						}
					>
						Please complete your profile to access all features
					</Alert>
				)}
				<Grid container spacing={4}>
					{/* Dashboard Statistics - Full Width */}
					<Grid item xs={10}>
						<DashboardStats />
					</Grid>

					{/* PatientRecords & Treatment Records - Side by Side */}
					<Grid item xs={10} md={6}>
						<div className={css(styles.paper, styles.largePaper)}>
							<PatientRecords />
						</div>
					</Grid>
					<Grid item xs={10} md={6}>
						<div className={css(styles.paper, styles.largePaper)}>
							<TreatmentRecords />
						</div>
					</Grid>

					{/* Appointments Calendar & Recent Activity - side by side */}
					<Grid item xs={12} md={6}>
						<div className={css(styles.paper)}>
							<AppointmentsCalendar />
						</div>
					</Grid>
					<Grid item xs={12} md={6}>
						<div className={css(styles.paper)}>
							<RecentActivity />
						</div>
					</Grid>
				</Grid>
			</div>
		</div>
	);
}

const styles = StyleSheet.create({
	container: {
		display: 'flex',
		backgroundColor: '#fff',
		overflow: 'auto',
	},
	title: {
		color: '#2783F7',
		marginBottom: '1.5rem',
		fontWeight: 'bold',
		fontSize: '1rem',
		'@media (max-width: 200px)': {
			fontSize: '1.5rem',
		},
	},
	content: {
		flexGrow: 1,
		padding: '20px',
		transition: 'margin 0.3s ease',
		marginLeft: 75, // Adjust for collapsed sidebar
	},
	contentShift: {
		marginLeft: 50, // Adjust for expanded sidebar
	},
	paper: {
		padding: '15px',
		width: '650px',
		display: 'flex',
		flexDirection: 'column',
		height: '370px',
		marginTop: '20px',
		marginRight: '20px',
		backgroundColor: '#fff',
		borderRadius: '8px',
		boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
	},
	largePaper: {
		height: '370px', // Extra space for calendar
	},
});
